import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { styles } from './styles';
import { injectGlobalCSS } from './globalStyles';

import IntroSequence from './components/introSequence/introSequence';
import HeroSignature from './components/heroSignature/heroSignature';
import Bookend from './components/bookend/bookend';
import Vae from './components/vae/vae';
import Intro from './pages/intro/intro';
import Services from './pages/services/services';
import Products from './pages/products/products';
import About from './pages/about/about';
import Contact from './pages/contact/contact';
import Legal from './pages/legal/legal';
import NotFound from './pages/notFound/notFound';
import Privacy from './pages/privacy/privacy';
import PlayerCafe from './pages/playerCafe/playerCafe';

const PRODUCT_BY_BRAND: Record<string, string> = {
  playercafe: 'playerCafe',
};

const brandProductId = (): string | undefined => {

  const brand = document.documentElement.getAttribute('data-brand');

  return brand ? PRODUCT_BY_BRAND[brand] : undefined;
};

const PRIVACY_PATH = '/privacy';

// The marketing page's own paths. `/index.html` is here because Hosting serves that file
// directly rather than rewriting it, so React sees it verbatim in the pathname.
const HOME_PATHS = ['', '/index.html'];

// Trailing slashes stripped so /legal and /legal/ are the same route; '' is the root.
const currentPath = (): string => window.location.pathname.replace(/\/+$/, '');

const App: React.FC = () => {
  useEffect(() => {
    injectGlobalCSS();
  }, []);

  const path = currentPath();

  // The legal documents are a standalone page — no hero, no intro sequence, no VAE widget.
  // Someone arriving here from the app, or from App Review, came to read a document.
  if (path === '/legal') {

    const legalId = brandProductId();

    return legalId ? <Legal productId={legalId} /> : <NotFound />;
  }

  if (path === PRIVACY_PATH) {
    return brandProductId() ? <NotFound /> : <Privacy />;
  }

  if (!HOME_PATHS.includes(path)) {
    return <NotFound />;
  }

  if (brandProductId() === 'playerCafe') {
    return <PlayerCafe />;
  }

  return (
    <div style={styles.app}>
      <div className="hero-signature" style={styles.signatureLayer}>
        <HeroSignature />
      </div>

      <IntroSequence />
      <Bookend isFooter={false} />

      <main>
        <Intro />
        <Services />
        <Products />
        <About />
        <Contact />
      </main>

      <Bookend isFooter={true} />

      <Vae />
    </div>
  );
};

// Inject the theme variables + base styles before first paint (avoids a flash).
injectGlobalCSS();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<App />);
